import React, { useEffect, useMemo } from 'react';

// Style
import "pages/MoleculeProduct/Status.scss";
import "./ImpuritiesForDrug.scss";

// Lib
import { shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useRegisterTutorialKey } from 'lib/hooks/useTutorial';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Actions
import { showMessage } from 'reducers/messages';
import * as actionsCreator from 'pages/ImpuritiesForDrug/reducer';

// Components
import Title from './compounds/Title';
import Loader from 'components/UiKit/Loader';
import ListDrugs from './compounds/ListDrugs';
import Breadcrumbs from 'components/Breadcrumbs';
import ScrollButton from 'components/ScrollButton';
import PaginationView from 'components/PaginationView';
import ListImpurities from './compounds/ListImpurities';
import RelatedCompounds from 'components/RelatedCompounds';

export const ImpuritiesForDrug = () => {
	const urlParam = useParams();
	const dispatch = useAppDispatch();
	const registerTutorialKey = useRegisterTutorialKey();

	const {
		data,
		goTo,
		paged,
		currPage,
		pageSize,
		loading,

		relatedCompounds,
		relatedCompoundsLoading

	} = useAppSelector(
		(store) => {
			return {
				data: store.impuritiesForDrug.data,
				goTo: store.impuritiesForDrug.goTo,
				paged: store.impuritiesForDrug.paged,
				loading: store.impuritiesForDrug.loading,
				currPage: store.impuritiesForDrug.currPage,
				pageSize: store.impuritiesForDrug.pageSize,

				relatedCompounds: store.impuritiesForDrug.relatedCompounds,
				relatedCompoundsLoading: store.impuritiesForDrug.relatedCompoundsLoading,
			};
		},
		shallowEqual
	);

	const drugName = data && data?.drugs && data?.drugs[0]?.drug || '';
	const { id } = urlParam;
	const drugs = data?.drugs;
	const impurities = data?.impurities;
	const total = Math.ceil((impurities?.length || 0) / (pageSize || 4));

	useEffect(() => {
		registerTutorialKey('drug-individual', () => !!drugs?.length, "normal", "general");
	}, [drugs]);

	useEffect(() => {
		window.scrollTo(0, 0);

		dispatch(actionsCreator.getProductInfoById(id));
		dispatch(actionsCreator.getRelatedCompounds(id));
	}, [id]);

	const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			const inputElement = e.target as HTMLInputElement;
			dispatch(actionsCreator.handlerChangeCurrPage(+inputElement.value - 1));
		}
	};

	const handlerChangeGoTo = (event: React.ChangeEvent<HTMLInputElement>) => {
		const number = +event?.target?.value > 0 ? +event?.target?.value : 1;

		if (number <= total) {
			dispatch(actionsCreator.handlerChangeGoTo(number));
		} else {
			dispatch(
				showMessage({
					type: 'info',
					text: 'This is the last record'
				})
			);
		}
	};

	const submitGoTo = () => {
		dispatch(actionsCreator.handlerChangeCurrPage((goTo || 1) - 1));
	};

	const onPagePress = (opt: number) => {
		dispatch(actionsCreator.handlerChangeCurrPage(opt - 1));
	};

	const onNextPressLast = () => {
		dispatch(actionsCreator.handlerChangeCurrPage(total - 1));
	};

	const onPrevPressLast = () => {
		dispatch(actionsCreator.handlerChangeCurrPage(0));
	};

	const newDataImpurities = useMemo(() => {
		if (paged && impurities?.length) {
			const start = (pageSize || 4) * currPage;
			const end = start + (pageSize || 4);
			return impurities.slice(start, end);
		}
		return impurities;
	}, [impurities, paged, currPage, pageSize]);

	return (
		<div className="impurities-for-drug relative">
			<ScrollButton />

			<div className="wrapper">
				{loading
					? <div className="impurities-for-drug__loading flex flex-ai-center flex-jc-center">
						<Loader type="circle" />
					</div>
					: <>
						<Breadcrumbs
							isLast
							className="mb10"
							title={drugName}
						/>

						<Title
							className="mb30"
							title={drugName}
							subTitle={`We provide ${drugs?.length} form${(drugs?.length || []) > 1 ? 's' : ''} of ${drugName}`}
						/>

						<ListDrugs data={drugs} />

						<RelatedCompounds
							title="Other forms:"
							data={relatedCompounds}
							loading={relatedCompoundsLoading}
							className="mb30 impurities-for-drug__related-compounds-title"
						/>

						<div
							className="impurities-for-drug__wrapper-title flex flex-ai-end flex-jc-between flex-direction-column-mobile mb30">
							<Title
								title="Impurities and Related Compounds"
								subTitle={`We provide ${(impurities?.length || 0)} compound${(impurities?.length || []) > 1 ? 's' : ''} for ${drugName}`}
							/>
							{((total || 0) > 1) && <PaginationView
								isWhite

								goTo={goTo}
								onKeyPress={onKeyPress}
								handlerSubmitGoTo={submitGoTo}
								handlerChangeGoTo={handlerChangeGoTo}

								number={currPage}
								totalPages={total}
								onPagePress={onPagePress}
								onNextPressLast={onNextPressLast}
								onPrevPressLast={onPrevPressLast}
							/>}
						</div>

						<ListImpurities data={newDataImpurities} />

						{((total || 0) > 1) && <PaginationView
							className="impurities-for-drug__pagination"

							isWhite

							goTo={goTo}
							onKeyPress={onKeyPress}
							handlerSubmitGoTo={submitGoTo}
							handlerChangeGoTo={handlerChangeGoTo}

							number={currPage}
							totalPages={total}
							onPagePress={onPagePress}
							onNextPressLast={onNextPressLast}
							onPrevPressLast={onPrevPressLast}
						/>}
					</>}
			</div>
		</div>
	);
};

export default ImpuritiesForDrug;
